import React, { useState, useEffect, useRef } from "react";
import FacebookPostCard from "./FacebookPostCard";
import img2 from "../../assets/images/kawa2.jpg";
import { getFileType } from "../../utils";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

const CARD_WIDTH = 300;
const GAP = 8;

const SliderSection = ({ posts, postTypeLabel }) => {
  const containerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(5);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  useEffect(() => {
    const updateVisibleCount = () => {
      const width = containerRef.current?.offsetWidth || 0;
      const count = Math.floor(width / (CARD_WIDTH + GAP));
      setVisibleCount(count || 1);
    };

    updateVisibleCount();
    window.addEventListener("resize", updateVisibleCount);
    return () => window.removeEventListener("resize", updateVisibleCount);
  }, []);

  useEffect(() => {
    const container = containerRef.current;

    const handleScroll = () => {
      if (!container) return;
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setIsAtStart(scrollLeft <= 5);
      setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 5);
    };

    if (container) {
      container.addEventListener("scroll", handleScroll);
      handleScroll();
    }

    return () => container?.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNext = () => {
    const container = containerRef.current;
    const scrollAmount = visibleCount * (CARD_WIDTH + GAP);
    container?.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  const handlePrev = () => {
    const container = containerRef.current;
    const scrollAmount = visibleCount * (CARD_WIDTH + GAP);
    container?.scrollBy({ left: -scrollAmount, behavior: "smooth" });
  };

  return (
    <div className="mb-10">
      <h1 className="font-bold text-sm mb-3">{postTypeLabel} Posts</h1>
      <div className="flex items-center gap-3 relative w-full">
        {posts.length > 0 && (
          <button
            disabled={isAtStart}
            onClick={handlePrev}
            className="text-xl px-3 disabled:opacity-30 z-10"
          >
            <IoIosArrowBack />
          </button>
        )}

        <div
          ref={containerRef}
          className="w-full overflow-auto duration-[1500ms] ease-in-out transition-all"
        >
          <div className="pl-2 pr-2 flex">
            {posts.map((item, index) => {
              const updatedImages =
                item?.images?.length > 0
                  ? item?.images?.map((img) => ({
                      ...img,
                      type: getFileType(img?.filename || img?.url),
                    }))
                  : [];

              return (
                <div
                  key={item._id || index}
                  className="w-[300px] max-w-[300px] px-1 flex-shrink-0"
                >
                  <FacebookPostCard
                    username={item?.accounts?.[0]?.accountName}
                    caption={item?.caption}
                    post={item}
                    file={
                      updatedImages?.length > 0
                        ? updatedImages
                        : [{ url: img2, type: getFileType(img2) }]
                    }
                    type={item?.platforms?.[0]?.platformName}
                    postType={postTypeLabel.toLowerCase()}
                    editAndDel={true}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {posts.length > 0 && (
          <button
            disabled={isAtEnd}
            onClick={handleNext}
            className="text-xl px-3 disabled:opacity-30 z-10"
          >
            <IoIosArrowForward />
          </button>
        )}
      </div>
    </div>
  );
};

const Posts = ({ recentPosts = [], upcomingPosts = [] }) => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const filteredRecentPosts = recentPosts.filter((item) => {
    const createdAt = new Date(item?.createdAt);
    return createdAt >= thirtyDaysAgo;
  });

  return (
    <div className="space-y-6 pb-10">
      <SliderSection posts={filteredRecentPosts} postTypeLabel="Recent" />
      <SliderSection posts={upcomingPosts} postTypeLabel="Upcoming" />
    </div>
  );
};

export default Posts;
